import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ChevronLeft, ChevronRight, Calendar, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Button } from '../ui/button';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/services/queryKeyFactory';
import { dashboardServices } from '@/services/dashboard.service';
import { useCompset } from '@/contexts/CompsetContext';
import DashboardError from '../error/DashboardError';
import { isToday } from 'date-fns';
import { formatDateYMD } from '@/helpers/formatters';

const DemandCalendar = () => {

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const { activeCompset } = useCompset();

  const { data: forecastData, isLoading: isLoadingForecastData, isError: isErrorForecastData } = useQuery({
    queryKey: queryKeys.demandEvents(activeCompset?.compset_id),
    queryFn: () => dashboardServices.getDemandsEvents(activeCompset?.compset_id),
    staleTime: 5 * 60 * 1000
  })

  const demandCardRef = useRef(null);

  // Create a map of date -> forecast? data for quick lookup
  const forecastMap = useMemo(() => {

    if (isLoadingForecastData || !forecastData) return {}

    const map = {};
    forecastData?.forecast?.forEach(day => {
      map[day.date] = day;
    });

    setSelectedDay(forecastData?.forecast?.[0]?.date || null); // Select first day with data by default

    return map;
  }, [forecastData, isLoadingForecastData]);

  // Get demand level and color
  const getDemandLevel = (demand) => {
    if (demand >= 85) return { level: 'very-high', color: 'bg-red-500', text: 'Very High', textColor: 'text-white' };
    if (demand >= 70) return { level: 'high', color: 'bg-orange-500', text: 'High', textColor: 'text-white' };
    if (demand >= 55) return { level: 'medium', color: 'bg-yellow-400', text: 'Medium', textColor: 'text-gray-900' };
    if (demand >= 40) return { level: 'low', color: 'bg-blue-400', text: 'Low', textColor: 'text-white' };
    return { level: 'very-low', color: 'bg-blue-200', text: 'Very Low', textColor: 'text-gray-700' };
  };

  // Get recommendation icon
  const getRecommendationIcon = (recommendation) => {
    switch (recommendation) {
      case 'increase_rates':
        return <TrendingUp className="w-3 h-3 text-green-600" />;
      case 'decrease_rates':
        return <TrendingDown className="w-3 h-3 text-red-600" />;
      default:
        return <Minus className="w-3 h-3 text-gray-500" />;
    }
  };

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // First day of month
    const firstDay = new Date(year, month, 1);
    const startingDay = firstDay.getDay();

    // Last day of month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();

    const days = [];

    // Previous month's trailing days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({
        date,
        dateStr: formatDateYMD(date),
        isCurrentMonth: false,
        day: prevMonthLastDay - i
      });
    }

    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day);
      days.push({
        date,
        dateStr: formatDateYMD(date),
        isCurrentMonth: true,
        day
      });
    }

    // Next month's leading days
    const remainingDays = 42 - days.length; // 6 rows x 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        dateStr: formatDateYMD(date),
        isCurrentMonth: false,
        day
      });
    }

    return days;
  }, [currentMonth]);


  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const currentMonthData = forecastData?.forecast?.filter(d => {
      const date = new Date(d.date);
      return date.getMonth() === currentMonth.getMonth() &&
        date.getFullYear() === currentMonth.getFullYear();
    });

    if (currentMonthData?.length === 0) return null;

    const avgDemand = currentMonthData?.reduce((sum, d) => sum + d.demand_percentage, 0) / currentMonthData?.length;
    const highDemandDays = currentMonthData?.filter(d => d.demand_percentage >= 70).length;
    const lowDemandDays = currentMonthData?.filter(d => d.demand_percentage < 50).length;
    const eventDays = currentMonthData?.filter(d => d.has_event).length;

    return {
      avgDemand: Math.round(avgDemand),
      highDemandDays,
      lowDemandDays,
      eventDays
    };
  }, [forecastData, currentMonth]);

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  if (isLoadingForecastData) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isErrorForecastData) {
    return <DashboardError
      section='Events and Forecast'
    />
  }

  return (
    <Card className="w-full" ref={demandCardRef}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Demand Forecast Calendar</CardTitle>
          </div>
        </div>
        <div className="text-xl font-semibold mt-2">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 mb-4 text-xs">
          <span className="text-muted-foreground font-medium">Demand Level:</span>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-red-500"></div>
            <span>Very High (85%+)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-orange-500"></div>
            <span>High (70-84%)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-yellow-400"></div>
            <span>Medium (55-69%)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-blue-400"></div>
            <span>Low (40-54%)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-blue-200"></div>
            <span>Very Low (&lt;40%)</span>
          </div>
        </div>


        {/* Summary Stats */}
        {summaryStats && (
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-primary">{summaryStats?.avgDemand ? `${summaryStats?.avgDemand}%` : "N/A"}</div>
              <div className="text-xs text-muted-foreground">Avg Demand</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-orange-500">{summaryStats?.highDemandDays ?? "N/A"}</div>
              <div className="text-xs text-muted-foreground">High Demand Days</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-500">{summaryStats?.lowDemandDays}</div>
              <div className="text-xs text-muted-foreground">Low Demand Days</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-purple-500">{summaryStats?.eventDays}</div>
              <div className="text-xs text-muted-foreground">Event Days</div>
            </div>
          </div>
        )}

        <div className="flex w-full justify-end mb-4 items-center gap-2">
          <button className="rounded-full p-3 bg-slate-200" onClick={goToPreviousMonth}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button className="rounded-full p-3 bg-slate-200" onClick={goToNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="border rounded-lg overflow-hidden">
          {/* Week day headers */}
          <div className="grid grid-cols-7 bg-muted/50">
            {weekDays.map(day => (
              <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground border-b">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7">
            <TooltipProvider>
              {calendarDays.map((dayInfo, index) => {
                const forecast = forecastMap[dayInfo?.dateStr];
                const demandInfo = forecast ? getDemandLevel(forecast.demand_percentage) : null;
                const hasData = forecast && dayInfo?.isCurrentMonth;

                return (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>
                      <div
                        className={`
                          relative p-1 min-h-[70px] border-b border-r cursor-pointer
                          transition-all duration-200 hover:ring-2 hover:ring-primary/50 hover:z-10
                          ${!dayInfo?.isCurrentMonth ? 'bg-muted/30' : ''}
                          ${isToday(dayInfo?.dateStr) ? 'ring-2 ring-primary' : ''}
                          ${selectedDay === dayInfo?.dateStr ? 'ring-2 ring-primary bg-primary/5' : ''}
                          ${hasData && (!!forecast?.has_event || !!forecast?.recommendation == "recommendation_text") ? "bg-orange-100" : ""}
                        `}
                        onClick={() => {
                          setSelectedDay(dayInfo?.dateStr)

                          if (demandCardRef.current && hasData) {
                            const cardBotton = demandCardRef.current?.clientHeight + demandCardRef.current?.offsetTop
                            window.scrollTo({
                              top: cardBotton,
                              behavior: 'smooth'
                            });
                          }
                        }}
                      >
                        {/* Day number */}
                        <div className={`
                          text-sm font-medium mb-1
                          ${!dayInfo?.isCurrentMonth ? 'text-muted-foreground/50' : ''}
                          ${isToday(dayInfo?.dateStr) ? 'text-primary font-bold' : ''}
                        `}>
                          {dayInfo?.day}
                        </div>

                        {/* Demand indicator */}
                        {hasData && (
                          <div className="space-y-1">
                            <div className={`
                              ${demandInfo.color} ${demandInfo.textColor}
                              rounded px-1.5 py-0.5 text-[10px] font-semibold text-center
                            `}>
                              {forecast?.demand_percentage}%
                            </div>

                            {/* Event indicator */}
                            {forecast?.has_event && (
                              <div className="flex justify-center">
                                <div className="w-2 h-2 rounded-full bg-purple-500" title="Event day"></div>
                              </div>
                            )}

                            {/* Recommendation icon */}
                            <div className="flex justify-center">
                              {getRecommendationIcon(forecast?.recommendation)}
                            </div>
                          </div>
                        )}
                      </div>
                    </TooltipTrigger>

                    {hasData && (
                      <TooltipContent
                        side="top"
                        className="max-w-xs bg-white text-gray-800 border border-gray-200 shadow-lg rounded-lg p-4"
                      >
                        <div className="space-y-3">

                          {/* Date */}
                          <div className="text-sm font-semibold text-gray-900">
                            {new Date(dayInfo?.dateStr)?.toLocaleDateString("en-US", {
                              weekday: "long",
                              month: "long",
                              day: "numeric",
                            })}
                          </div>
                          <span className='mt-1 text-xs text-secondary-foreground'> Click to see details </span>

                          {/* Demand */}
                          <div className="flex items-center gap-2">
                            <Badge className={`${demandInfo?.color} ${demandInfo?.textColor}`}>
                              {forecast?.demand_percentage}% Demand
                            </Badge>
                            <span className="text-xs text-gray-500">
                              ({demandInfo?.text})
                            </span>
                          </div>

                          {/* Demand details */}
                          <div className="text-xs space-y-1 text-gray-600">
                            <div className="flex justify-between">
                              <span>Base Demand</span>
                              <span className="font-medium">{forecast?.base_demand}%</span>
                            </div>

                            {forecast?.event_boost > 0 && (
                              <div className="flex justify-between text-purple-600">
                                <span>Event Boost</span>
                                <span className="font-medium">
                                  +{forecast?.event_boost}%
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Events */}
                          {forecast?.has_event && forecast?.events?.length > 0 && (
                            <div className="border-t pt-2">
                              <div className="text-xs font-medium text-gray-700 mb-1">
                                Events
                              </div>

                              <div className="space-y-1">
                                {forecast?.events?.map((event, i) => (
                                  <div
                                    key={i}
                                    className="text-xs text-gray-500 flex justify-between"
                                  >
                                    <span>{event?.name}</span>
                                    <span className="text-purple-600 font-medium">
                                      +{event?.impact}%
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Recommendation */}
                          <div className="border-t pt-2 text-xs flex flex-col justify-between ">
                            <p className="font-medium text-gray-700">
                              Recommendation
                            </p>

                            <p
                              className={`font-semibold ${forecast?.recommendation === "increase_rates"
                                ? "text-green-600"
                                : forecast?.recommendation === "decrease_rates"
                                  ? "text-red-600"
                                  : "text-gray-600"
                                }`}
                            >
                              {forecast?.recommendation_text}
                            </p>
                          </div>
                        </div>
                      </TooltipContent>
                    )}
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </div>
        </div>

        {/* Selected Day Details */}
        {selectedDay && forecastMap[selectedDay] && (
          <div className="mt-4 p-4 bg-muted/30 rounded-lg border">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold">
                {new Date(selectedDay).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </h4>
              <Button variant="ghost" size="sm" onClick={() => setSelectedDay(null)}>
                Clear
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-muted-foreground">Predicted Demand</div>
                <div className="text-2xl font-bold">{forecastMap[selectedDay]?.demand_percentage}%</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Base Demand</div>
                <div className="text-lg font-semibold">{forecastMap[selectedDay]?.base_demand}%</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Event Boost</div>
                <div className="text-lg font-semibold text-purple-600">+{forecastMap[selectedDay]?.event_boost}%</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Recommendation</div>
                <Badge variant={
                  forecastMap[selectedDay]?.recommendation === 'increase_rates' ? 'default' :
                    forecastMap[selectedDay]?.recommendation === 'decrease_rates' ? 'destructive' :
                      'secondary'
                }>
                  {forecastMap[selectedDay]?.recommendation === 'increase_rates' ? 'Increase Rates' :
                    forecastMap[selectedDay]?.recommendation === 'decrease_rates' ? 'Decrease Rates' :
                      'Maintain Rates'}
                </Badge>
              </div>
            </div>

            {forecastMap[selectedDay]?.has_event && forecastMap[selectedDay]?.events?.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <div className="text-sm font-medium mb-2">Affecting Events:</div>
                <div className="space-y-1">
                  {forecastMap[selectedDay]?.events.map((event, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-purple-50 dark:bg-purple-950/30 rounded px-2 py-1">
                      <span>{event?.name}</span>
                      <Badge variant="outline" className="text-purple-600">+{event?.impact}% demand</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DemandCalendar;
